// src/pages/task/TaskSettingsPage.tsx

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useTaskStatusDefinition, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  ErrorAlert,
  Badge,
} from '@react/components';
import { Select } from '@/components/Select';
import { IconPicker } from '@/components/IconPicker';

import {
  baseCategoryOptions,
  getStatusMapping,
  taskStatusMap,
  validateStatusDefinition,
} from '@/features/task/logic';

import type { TaskStatusDefinition } from '@core/types/task';

const DEFAULT_COLORS = [
  '#3b82f6', '#eab308', '#f97316', '#ef4444', '#22c55e',
  '#8b5cf6', '#ec4899', '#6b7280',
];

/**
 * Task settings page.
 * Custom status definitions — the only real, backend-backed configuration
 * surface here. View/notification/label/recurring "preferences" further down
 * this page used to be localStorage-only mockups with no server support and
 * have been removed; see /settings/tasks for the real admin surfaces
 * (custom fields, categories, numbering, policies, container configs).
 */
export const TaskSettingsPage = observer(() => {
  const store = useTask();
  const statusDefStore = useTaskStatusDefinition();
  const { t } = useI18n();

  // Load custom status definitions on mount
  useEffect(() => {
    statusDefStore.ListStatusDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New status definition form
  const [newStatusSlug, setNewStatusSlug] = useState('');
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusDescription, setNewStatusDescription] = useState('');
  const [newStatusCategory, setNewStatusCategory] = useState('');
  const [newStatusColor, setNewStatusColor] = useState(DEFAULT_COLORS[0]);
  const [newStatusIcon, setNewStatusIcon] = useState('');
  const [statusFormErrors, setStatusFormErrors] = useState<Record<string, string>>({});
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Editing an existing status definition — UpdateStatusDefinition. Slug is
  // immutable once created, so only name/description/category/color/icon are editable.
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  const sortedDefinitions = [...statusDefStore.items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const handleAddStatusDefinition = async () => {
    const errors = validateStatusDefinition({
      slug: newStatusSlug,
      display_name: newStatusName,
      category: newStatusCategory,
    });
    setStatusFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (editingStatusId) {
        const existing = statusDefStore.items.find((d: TaskStatusDefinition) => d.entity?.id === editingStatusId);
        await statusDefStore.UpdateStatusDefinition(editingStatusId, {
          definition: {
            ...existing,
            display_name: newStatusName.trim(),
            description: newStatusDescription.trim(),
            category: newStatusCategory as any,
            color: newStatusColor,
            icon: newStatusIcon,
          } as TaskStatusDefinition,
          field_mask: ['display_name', 'description', 'category', 'color', 'icon'],
        });
        setEditingStatusId(null);
      } else {
        await statusDefStore.CreateStatusDefinition({
          slug: newStatusSlug.trim(),
          display_name: newStatusName.trim(),
          description: newStatusDescription.trim(),
          category: newStatusCategory as any,
          color: newStatusColor,
          icon: newStatusIcon,
          position: statusDefStore.items.length,
        });
      }
      setNewStatusSlug('');
      setNewStatusName('');
      setNewStatusDescription('');
      setNewStatusCategory('');
      setNewStatusColor(DEFAULT_COLORS[0]);
      setNewStatusIcon('');
      setStatusFormErrors({});
    } catch {
      // Error is shown via store.error
    }
  };

  const startEditStatusDefinition = (def: TaskStatusDefinition) => {
    setEditingStatusId(def.entity?.id || null);
    setNewStatusSlug(def.slug || '');
    setNewStatusName(def.display_name || '');
    setNewStatusDescription(def.description || '');
    setNewStatusCategory(typeof def.category === 'string' ? def.category : '');
    setNewStatusColor(def.color || DEFAULT_COLORS[0]);
    setNewStatusIcon(def.icon || '');
    setStatusFormErrors({});
  };

  const cancelEditStatusDefinition = () => {
    setEditingStatusId(null);
    setNewStatusSlug('');
    setNewStatusName('');
    setNewStatusDescription('');
    setNewStatusCategory('');
    setNewStatusColor(DEFAULT_COLORS[0]);
    setNewStatusIcon('');
    setStatusFormErrors({});
  };

  const handleDeleteStatusDefinition = async (id: string) => {
    try {
      await statusDefStore.DeleteStatusDefinition(id);
      setDeleteConfirmId(null);
    } catch {
      // Error is shown via store.error
    }
  };

  const moveStatusDefinition = async (id: string, direction: 'up' | 'down') => {
    const idx = sortedDefinitions.findIndex((d) => d.entity?.id === id);
    if (idx === -1) return;
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= sortedDefinitions.length) return;
    const current = sortedDefinitions[idx];
    const neighbor = sortedDefinitions[neighborIdx];
    const currentId = current.entity?.id;
    const neighborId = neighbor.entity?.id;
    if (!currentId || !neighborId) return;

    setReorderingId(id);
    try {
      await Promise.all([
        statusDefStore.UpdateStatusDefinition(currentId, {
          definition: { ...current, position: neighbor.position },
          field_mask: ['position'],
        }),
        statusDefStore.UpdateStatusDefinition(neighborId, {
          definition: { ...neighbor, position: current.position },
          field_mask: ['position'],
        }),
      ]);
    } catch {
      // Error is shown via store.error
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <nav className="text-sm">
          <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
            {t('task.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('task.settings')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('task.settings')}</h1>
        <div className="mt-2 flex flex-wrap gap-3 text-sm">
          <Link to="/tasks/ready" className="text-primary hover:underline">{t('task.ready.title')}</Link>
          <Link to="/tasks/graph" className="text-primary hover:underline">{t('task.graph.title')}</Link>
          <Link to="/tasks/stats" className="text-primary hover:underline">{t('task.statistics')}</Link>
          {/* Views live in the workspace itself now — the strip above the task
              list — so this points at the workspace rather than a CRUD page. */}
          <Link to="/tasks" className="text-primary hover:underline">{t('task.views')}</Link>
          <Link to="/settings/tasks" className="text-primary hover:underline">{t('task.admin.title')}</Link>
        </div>
      </div>

      {/* Error state */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {statusDefStore.error ? <ErrorAlert error={statusDefStore.error} dismissible onDismiss={() => statusDefStore.clearError()} /> : null}

      {/* Custom Statuses */}
      <Card>
        <CardHeader>
          <CardTitle>{t('task.statusDef.title')}</CardTitle>
          <CardDescription>{t('task.statusDef.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing status definitions */}
          {statusDefStore.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-md border border-border animate-pulse">
                  <div className="h-4 w-4 rounded-full bg-muted" />
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-4 w-16 bg-muted rounded ml-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {sortedDefinitions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('task.statusDef.empty')}</p>
              ) : (
                sortedDefinitions.map((def, idx) => {
                  const categoryInfo = getStatusMapping(taskStatusMap, typeof def.category === 'string' ? def.category : '');
                  const defId = def.entity?.id || '';
                  const isBusy = reorderingId === defId;
                  return (
                    <div
                      key={defId}
                      className="flex items-center justify-between py-2 px-3 rounded-md border border-border gap-3"
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveStatusDefinition(defId, 'up')}
                          disabled={idx === 0 || isBusy}
                          aria-label={t('task.statusDef.moveUp')}
                          className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 15l-6-6-6 6" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStatusDefinition(defId, 'down')}
                          disabled={idx === sortedDefinitions.length - 1 || isBusy}
                          aria-label={t('task.statusDef.moveDown')}
                          className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span
                          className="inline-block h-4 w-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: def.color || '#6b7280' }}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground font-medium truncate">{def.display_name}</span>
                            <span className="text-xs text-muted-foreground">{def.slug}</span>
                          </div>
                          {def.description && (
                            <p className="text-xs text-muted-foreground truncate">{def.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={categoryInfo.variant} size="sm">
                          {t(categoryInfo.labelKey)}
                        </Badge>
                        {deleteConfirmId === defId ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteStatusDefinition(defId)}
                              isLoading={statusDefStore.isDeleting}
                            >
                              {t('common.confirm')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditStatusDefinition(def)}
                            >
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => setDeleteConfirmId(defId)}
                            >
                              {t('common.delete')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Add / edit a status definition */}
          <div className="pt-4 border-t border-border space-y-3">
            <h4 className="text-sm font-medium text-foreground">
              {editingStatusId ? t('task.statusDef.editTitle') : t('task.statusDef.addNew')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Input
                  label={t('task.statusDef.displayName')}
                  type="text"
                  value={newStatusName}
                  onChange={(e) => setNewStatusName(e.target.value)}
                  placeholder={t('task.statusDef.displayNamePlaceholder')}
                  error={statusFormErrors.display_name ? t(statusFormErrors.display_name) : undefined}
                />
              </div>
              <div>
                <Input
                  label={t('task.statusDef.slug')}
                  type="text"
                  value={newStatusSlug}
                  onChange={(e) => setNewStatusSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  placeholder="code_review"
                  error={statusFormErrors.slug ? t(statusFormErrors.slug) : undefined}
                  disabled={!!editingStatusId}
                />
              </div>
            </div>

            <Input
              label={t('task.field.description')}
              type="text"
              value={newStatusDescription}
              onChange={(e) => setNewStatusDescription(e.target.value)}
              placeholder={t('task.statusDef.descriptionPlaceholder')}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.statusDef.category')}</label>
                <Select value={newStatusCategory} onChange={(e) => setNewStatusCategory(e.target.value)}>
                  <option value="">{t('task.statusDef.selectCategory')}</option>
                  {baseCategoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </Select>
                {statusFormErrors.category && (
                  <p className="mt-1 text-xs text-destructive">{t(statusFormErrors.category)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.statusDef.color')}</label>
                <div className="flex gap-1.5">
                  {DEFAULT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewStatusColor(color)}
                      className={`h-8 w-8 rounded-full border-2 transition-all ${
                        newStatusColor === color ? 'border-foreground scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.statusDef.icon')}</label>
              <IconPicker value={newStatusIcon} onChange={setNewStatusIcon} />
            </div>

            <div className="flex justify-end gap-2">
              {editingStatusId && (
                <Button variant="ghost" onClick={cancelEditStatusDefinition} disabled={statusDefStore.isSaving}>
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                onClick={handleAddStatusDefinition}
                disabled={!newStatusName.trim() || !newStatusSlug.trim() || !newStatusCategory}
                isLoading={statusDefStore.isSaving}
              >
                {editingStatusId ? t('common.save') : t('task.statusDef.add')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default TaskSettingsPage;
